// BulkSelectBar — the action bar for kitchen multi-select mode.
//
// Cleanup is a batch chore ("this whole meat drawer is stale, the
// veggies are long eaten") and the per-card hold-menu made it a
// one-at-a-time grind. Select mode turns it into: SELECT → tap cards
// (or ALL) → REMOVE n → confirm tap. "Clear the drawer" is just
// ALL + REMOVE — no separate destructive button to misfire.
//
// Two-tap confirm matches the house delete pattern (KitchenCard /
// CheckoutScreen): first tap arms (label flips to TAP TO CONFIRM in
// rose), second commits, 4s auto-disarm. The caller shows an UNDO
// toast after commit, so the worst case is two taps to recover.
//
// Fixed above the bottom nav; lifts with the keyboard via the
// universal --kb-offset (not that selecting needs typing — belt and
// suspenders).

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FONT, COLOR } from "../../lib/tokens";

const ARM_MS = 4000;

export default function BulkSelectBar({
  count,          // selected row count (physical rows, incl. stack members)
  total,          // selectable row count in the current view
  onSelectAll,
  onClearSelection,
  onCancel,
  onRemove,       // commit — caller deletes + toasts undo
  onRestock = null,  // selected → shopping list (items stay)
  onPin = null,      // selected canonicals → AI-recipe pin store
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), ARM_MS);
    return () => clearTimeout(t);
  }, [armed]);
  // Disarm when the selection changes out from under the armed state.
  useEffect(() => { setArmed(false); }, [count]);

  const allSelected = total > 0 && count >= total;

  // Portal to <body> — KitchenScreen lives inside framer-motion's
  // transformed tab container, and a transformed ancestor hijacks
  // position:fixed (the bar anchored to the bottom of the SCROLL
  // CONTENT, i.e. off-screen). Every fixed overlay in the app
  // portals for the same reason.
  const none = count === 0;
  const actionBtn = (enabled) => ({
    flex: 1, padding: "10px 6px",
    background: "transparent",
    border: `1px solid ${COLOR.border}`,
    color: enabled ? COLOR.ink : COLOR.sub,
    borderRadius: 10,
    fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
    letterSpacing: "0.06em",
    cursor: enabled ? "pointer" : "default",
    touchAction: "manipulation",
  });

  // Cream panel, NOT a dark slab — the token set is the cream
  // register (COLOR.ink is dark #1E1E1E), so a dark bar rendered
  // every label black-on-black. Strong shadow + border separate the
  // floating bar from the cream page beneath.
  return createPortal(
    <div style={{
      position: "fixed",
      left: 12, right: 12,
      bottom: "calc(76px + env(safe-area-inset-bottom) + var(--kb-offset, 0px))",
      zIndex: 140,
      display: "flex", flexDirection: "column", gap: 8,
      padding: "10px 12px",
      background: "rgba(250,246,238,0.98)",
      border: `1px solid ${COLOR.border}`,
      borderRadius: 16,
      boxShadow: "0 12px 28px rgba(40,28,12,0.30), 0 2px 8px rgba(40,28,12,0.18)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
    }}>
      {/* Row 1 — mode controls: exit, count, select-all. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          aria-label="Exit select mode"
          className="mcm-focusable"
          onClick={onCancel}
          style={{
            width: 34, height: 34, flexShrink: 0, borderRadius: 17,
            background: "transparent", border: `1px solid ${COLOR.border}`,
            color: COLOR.muted, fontFamily: FONT.mono, fontSize: 14,
            cursor: "pointer", touchAction: "manipulation",
          }}
        >
          ×
        </button>
        <div style={{
          flex: 1, minWidth: 0,
          fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
          color: COLOR.ink, letterSpacing: "0.08em",
        }}>
          {count} SELECTED
        </div>
        <button
          type="button"
          className="mcm-focusable"
          onClick={allSelected ? onClearSelection : onSelectAll}
          style={{
            padding: "9px 13px", flexShrink: 0,
            background: "transparent", border: `1px solid ${COLOR.border}`,
            color: COLOR.dim, borderRadius: 10,
            fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.08em", cursor: "pointer",
            touchAction: "manipulation",
          }}
        >
          {allSelected ? "NONE" : "ALL"}
        </button>
      </div>
      {/* Row 2 — the actions that used to live on the card's flip
          panel, now batch-capable. REMOVE keeps the two-tap confirm. */}
      <div style={{ display: "flex", gap: 8 }}>
        {onPin && (
          <button
            type="button"
            className="mcm-focusable"
            disabled={none}
            onClick={() => { if (!none) onPin(); }}
            style={actionBtn(!none)}
          >
            TO RECIPE
          </button>
        )}
        {onRestock && (
          <button
            type="button"
            className="mcm-focusable"
            disabled={none}
            onClick={() => { if (!none) onRestock(); }}
            style={actionBtn(!none)}
          >
            RESTOCK
          </button>
        )}
        <button
          type="button"
          className="mcm-focusable"
          disabled={none}
          onClick={() => {
            if (none) return;
            if (!armed) { setArmed(true); return; }
            setArmed(false);
            onRemove();
          }}
          style={{
            flex: 1.2, padding: "10px 6px",
            background: none ? "transparent"
              : armed ? "linear-gradient(180deg, #C85A1F 0%, #A34711 100%)" : COLOR.roseDeep,
            border: `1px solid ${none ? COLOR.border : COLOR.roseDim}`,
            color: none ? COLOR.sub : armed ? "#FFF8EE" : "#A34711",
            borderRadius: 10,
            fontFamily: FONT.mono, fontSize: 12, fontWeight: 700,
            letterSpacing: "0.06em",
            cursor: none ? "default" : "pointer",
            touchAction: "manipulation",
          }}
        >
          {armed ? "TAP TO CONFIRM" : `REMOVE${count ? ` ${count}` : ""}`}
        </button>
      </div>
    </div>,
    document.body,
  );
}
